import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { toast } from '../../../stores/toast.store';
import { confirm } from '../../../stores/confirm.store';
import { fmtEur, fmtDate } from '../../../lib/utils';
import type { TaxGroup, TaxKind, TaxSource } from '@masamune/shared';
import type { FiscoVista } from '../../../lib/adminRoutes';

/**
 * Fisco: i dati delle viste. I conti li fa l'API (apps/api/src/modules/admin/
 * admin.fisco.ts): gruppi, totali, frasi, tempo in parole. Qui solo i tipi, le
 * query e l'unica azione nuova (confermare un possibile pagamento). Le altre
 * azioni restano quelle di sempre: "Segna pagata" su /admin/uscite/pay,
 * modifica ed eliminazione su /admin/taxes.
 *
 * Query key sotto ['admin', ...]: ogni azione dell'Amministrazione le rinfresca.
 */

export type StatoVersamento = 'NON_VERSATA' | 'A_RATE' | 'DA_PAGARE' | 'PAGATA' | 'NEL_PIANO';

export interface TassaLite {
  id: string;
  kind: TaxKind;
  year: number;
  period: string | null;
  description: string;
  amountDue: number;
  dueDate: string | null;
  source: TaxSource;
  note: string | null;
  entity: 'MASAMUNE' | 'PERSONALE_ANDREA';
  status: string;
  rate: number;
}

export interface PossibilePagamento {
  txIds: string[];
  n: number;
  totale: number;
  differenza: number;
  testo: string;
  ultimo: string;
}

export interface RigaFisco {
  key: string;
  obligationId: string | null;
  installmentId: string | null;
  kind: TaxKind;
  group: TaxGroup;
  nome: string;
  dettaglio: string | null;
  data: string | null;
  giorni: number | null;
  quando: string;
  importo: number | null;
  stato: StatoVersamento;
  pagataIl: string | null;
  fonte: TaxSource;
  fonteSpiegazione: string | null;
  rata: { n: number; di: number } | null;
  diAndrea: boolean;
  possibile: PossibilePagamento | null;
  inserisciCifra: boolean;
  tax: TassaLite | null;
  vai: TaxGroup | null;
  n: number | null;
}

export interface VoceFascia {
  key: string;
  label: string;
  valore: number | null;
  testo: string | null;
  sotto: string | null;
  aiuto: string | null;
  tono: 'alert' | null;
  fonte: TaxSource | null;
}

export interface GruppoCalendario {
  key: 'indietro' | 'entro30' | 'entro90';
  label: string;
  totale: number;
  senzaCifra: number;
  righe: RigaFisco[];
}

export interface DaClassificare { id: string; data: string; dataTesto: string; importo: number; tipo: string; serie: string | null }

export interface Sintesi {
  oggi: string;
  anno: number;
  frase: string;
  gruppi: GruppoCalendario[];
  pagate: {
    label: string;
    totale: number;
    perGruppo: Array<{ group: TaxGroup; label: string; n: number; totale: number }>;
    daClassificare: DaClassificare[];
  };
  piuAvanti: { n: number; ultima: string | null; testo: string | null };
}

export interface RigaIva extends RigaFisco { trimestre: string; anno: number; q: number | null }

export interface PianoRate {
  obligationId: string;
  nome: string;
  numero: string | null;
  totale: number;
  resta: number;
  versato: number;
  rate: number;
  pagate: number;
  nonPagateInTempo: number;
  rataMese: number | null;
  prossima: { n: number; data: string; quando: string; dataTesto: string } | null;
  ultima: string | null;
  ultimaTesto: string | null;
  dentro: Array<{ cosa: string; importo: number }>;
  ratePagate: RigaFisco[];
  rateDiverse: RigaFisco[];
  rateUguali: { n: number; daN: number; aN: number; primaTesto: string; ultimaTesto: string } | null;
  tax: TassaLite;
}

export interface SerieChiusa { key: string; n: number; importo: number; tipo: string; testo: string }

export interface VistaGruppo {
  group: TaxGroup;
  label: string;
  frase: string;
  fascia: VoceFascia[];
  righe: RigaFisco[];
  pagate: RigaFisco[];
  iva?: {
    anni: Array<{ anno: number; resta: number | null; righe: RigaIva[] }>;
    notaAnticipi: string | null;
    anticipo: { anno: number; data: string; dataTesto: string; tax: TassaLite | null };
  };
  piani?: PianoRate[];
  serieChiuse?: SerieChiusa[];
}

export const fiscoSintesiQuery = {
  queryKey: ['admin', 'fisco', 'sintesi'] as const,
  queryFn: () => api.get<Sintesi>('/admin/fisco/sintesi'),
  staleTime: 30_000,
};

export function fiscoGruppoQuery(group: TaxGroup) {
  return {
    queryKey: ['admin', 'fisco', 'gruppo', group] as const,
    queryFn: () => api.get<VistaGruppo>(`/admin/fisco/gruppo/${group}`),
    staleTime: 30_000,
  };
}

/** La chiave della vista (`?vista=`) per ogni gruppo di tasse, e ritorno. */
export const VISTA_DI_GRUPPO: Record<TaxGroup, FiscoVista> = {
  F24_DIPENDENTI: 'f24',
  IVA: 'iva',
  SOCIETA: 'imposte',
  RATE: 'rate',
  ALTRI: 'altri',
};

/**
 * "Conferma il pagamento": collega a una rata i movimenti che la banca
 * propone e la segna pagata. Prima chiede, con le parole esatte di cosa
 * succede (compreso il cambio di cifra del ravvedimento).
 */
export function useConfermaPossibile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: RigaFisco) => {
      const p = r.possibile!;
      const diff = p.differenza > 0.005;
      const ok = await confirm({
        title: 'Confermi il pagamento?',
        icon: 'check',
        confirmLabel: 'Sì, è pagata',
        body: (
          `Collego ${p.testo} a "${r.nome}" e la segno pagata il ${fmtDate(p.ultimo)}.`
          + (diff
            ? ` La cifra passa da € ${fmtEur(r.importo, true)} a € ${fmtEur(p.totale, true)}: la differenza di € ${fmtEur(p.differenza, true)} è il ravvedimento (sanzioni e interessi), e resta scritta nella nota.`
            : '')
        ),
      });
      if (!ok) return null;
      return api.post<{ totale: number }>('/admin/fisco/possibili/conferma', { installmentId: r.installmentId, transactionIds: p.txIds });
    },
    onSuccess: (res, r) => {
      if (!res) return;
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok('Pagamento collegato', `${r.nome} · € ${fmtEur(res.totale, true)}`);
    },
    onError: (e) => toast.alert('Pagamento non collegato', e instanceof ApiError ? e.message : 'Riprova'),
  });
}
